import { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ServerConnectionContext } from './ServerConnection';
import { ServerDataContext } from './ServerData';

type TargetMode = 'all' | 'organization' | 'user';

const PageContainer = styled.div`
  max-width: 600px;
`;

const Title = styled.h2`
  margin-bottom: 8px;
  color: #333;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #b31b1b;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #b31b1b;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #b31b1b;
  }
`;

const Button = styled.button<{ disabled?: boolean }>`
  background-color: ${props => (props.disabled ? '#ccc' : '#b31b1b')};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => (props.disabled ? '#ccc' : '#8b1515')};
  }
`;

const ResultBox = styled.div<{ success: boolean }>`
  margin-top: 20px;
  padding: 12px 16px;
  border-radius: 4px;
  background-color: ${props => (props.success ? '#e8f5e9' : '#ffebee')};
  color: ${props => (props.success ? '#2e7d32' : '#c62828')};
  border: 1px solid ${props => (props.success ? '#a5d6a7' : '#ef9a9a')};
`;

const InfoBox = styled.div`
  margin-top: 20px;
  padding: 12px 16px;
  border-radius: 4px;
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
  font-size: 14px;
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  max-height: 240px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
`;

const SearchResultItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  background: white;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fafafa;
  }
`;

const SearchResultEmpty = styled.div`
  padding: 10px 12px;
  color: #999;
  font-size: 14px;
  font-style: italic;
`;

const UserSecondaryText = styled.div`
  color: #888;
  font-size: 12px;
  margin-top: 2px;
`;

const SelectedUser = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #b31b1b;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;

  &:hover {
    text-decoration: underline;
  }
`;

interface NotificationResult {
  successCount: number;
  failureCount: number;
  errors?: string[];
}

export function Notifications() {
  const connection = useContext(ServerConnectionContext);
  const serverData = useContext(ServerDataContext);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetMode, setTargetMode] = useState<TargetMode>('all');
  const [organizationId, setOrganizationId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<NotificationResult | null>(null);

  const organizations = Array.from(serverData.organizations.values());
  const allUsers = Array.from(serverData.users.values());
  const selectedUser = selectedUserId
    ? serverData.users.get(selectedUserId)
    : undefined;

  // Filter users by username or email; show top 25 to keep the list responsive
  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    if (!query) {
      return allUsers.slice(0, 25);
    }
    return allUsers
      .filter(
        u =>
          u.username?.toLowerCase().includes(query) ||
          u.email?.toLowerCase().includes(query),
      )
      .slice(0, 25);
  }, [allUsers, userSearch]);

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    setUserSearch('');
    setSearchFocused(false);
  };

  const handleClearUser = () => {
    setSelectedUserId('');
    setUserSearch('');
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const payload: {
        title: string;
        body: string;
        organizationId?: string;
        userIds?: string[];
      } = {
        title: title.trim(),
        body: body.trim(),
      };

      if (targetMode === 'organization' && organizationId) {
        payload.organizationId = organizationId;
      } else if (targetMode === 'user' && selectedUserId) {
        payload.userIds = [selectedUserId];
      }

      const response = await connection.connection
        ?.timeout(10000)
        .emitWithAck('sendNotification', payload);

      if (response) {
        setResult(response as NotificationResult);
        if (response.successCount > 0) {
          // Clear form on success
          setTitle('');
          setBody('');
        }
      } else {
        setResult({
          successCount: 0,
          failureCount: 0,
          errors: ['No response from server'],
        });
      }
    } catch (error) {
      setResult({
        successCount: 0,
        failureCount: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      });
    } finally {
      setSending(false);
    }
  };

  const targetIsValid =
    targetMode === 'all' ||
    (targetMode === 'organization' && organizationId !== '') ||
    (targetMode === 'user' && selectedUserId !== '');

  const canSend = title.trim() && body.trim() && targetIsValid && !sending;

  return (
    <PageContainer>
      <Title>Send Push Notification</Title>
      <Subtitle>
        Send a notification to users' devices. This will appear in their
        notification panel.
      </Subtitle>

      <FormGroup>
        <Label>Target Audience</Label>
        <Select
          value={targetMode}
          onChange={e => {
            setTargetMode(e.target.value as TargetMode);
            setOrganizationId('');
            setSelectedUserId('');
            setUserSearch('');
          }}
        >
          <option value="all">All Users</option>
          <option value="organization">Specific Organization</option>
          <option value="user">Specific User</option>
        </Select>
      </FormGroup>

      {targetMode === 'organization' && (
        <FormGroup>
          <Label>Organization</Label>
          <Select
            value={organizationId}
            onChange={e => setOrganizationId(e.target.value)}
          >
            <option value="">Select an organization…</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </Select>
        </FormGroup>
      )}

      {targetMode === 'user' && (
        <FormGroup>
          <Label>User</Label>
          {selectedUser ? (
            <SelectedUser>
              <div>
                <div>{selectedUser.username || '(no username)'}</div>
                {selectedUser.email && (
                  <UserSecondaryText>{selectedUser.email}</UserSecondaryText>
                )}
              </div>
              <ClearButton onClick={handleClearUser}>Change</ClearButton>
            </SelectedUser>
          ) : (
            <SearchContainer>
              <Input
                type="text"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() =>
                  // Delay so click on result registers before close
                  setTimeout(() => setSearchFocused(false), 150)
                }
                placeholder="Search by username or email…"
              />
              {searchFocused && (
                <SearchResults>
                  {filteredUsers.length === 0 ? (
                    <SearchResultEmpty>No users found</SearchResultEmpty>
                  ) : (
                    filteredUsers.map(u => (
                      <SearchResultItem
                        key={u.id}
                        type="button"
                        onMouseDown={e => {
                          e.preventDefault();
                          handleSelectUser(u.id);
                        }}
                      >
                        <div>{u.username || '(no username)'}</div>
                        {u.email && (
                          <UserSecondaryText>{u.email}</UserSecondaryText>
                        )}
                      </SearchResultItem>
                    ))
                  )}
                </SearchResults>
              )}
            </SearchContainer>
          )}
        </FormGroup>
      )}

      <FormGroup>
        <Label>Notification Title</Label>
        <Input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g., New Event Available!"
          maxLength={100}
        />
      </FormGroup>

      <FormGroup>
        <Label>Notification Message</Label>
        <TextArea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="e.g., Check out the new campus scavenger hunt starting this weekend!"
          maxLength={500}
        />
      </FormGroup>

      <Button onClick={handleSend} disabled={!canSend}>
        {sending ? 'Sending...' : 'Send Notification'}
      </Button>

      {result && (
        <ResultBox success={result.successCount > 0 && !result.errors?.length}>
          {result.successCount > 0 ? (
            <>
              Successfully sent to <strong>{result.successCount}</strong>{' '}
              {result.successCount === 1 ? 'user' : 'users'}
              {result.failureCount > 0 && <> ({result.failureCount} failed)</>}
            </>
          ) : (
            <>
              Failed to send notification
              {result.errors?.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </>
          )}
        </ResultBox>
      )}

      <InfoBox>
        <strong>Note:</strong> Only users who have enabled notifications and
        have opened the app recently will receive this notification.
      </InfoBox>
    </PageContainer>
  );
}
